import toast from 'react-hot-toast';

import { buildDeepLink } from '../components/Banners/deepLinkTargets';
import { referenceEntityId } from './entityLinks';

/**
 * Public, shareable https links to app content — the thing an admin pastes into
 * a social post, an email or a QR code.
 *
 * The PATH is not built here. It comes from `buildDeepLink`, the same builder
 * the push composer and the banner editor store, so a shared link and a push
 * deep link to the same entity can never disagree about the route. This module
 * only adds the universal-link origin in front of it.
 *
 * The origin is the Firebase-hosted site that carries the apple-app-site-
 * association and assetlinks files (watchlock `hosting/`). With the app
 * installed the OS opens it in the app; without it the hosted fallback page
 * offers the stores. The RN `DeepLinkService` strips this same origin, so the
 * path after it routes exactly as a push `deepLink` does.
 */
export const APP_LINK_ORIGIN = 'https://app.watchkeeper.tech';

/** The app's own URL scheme. The RN `navigateToDeepLink` strips it exactly like the https origin. */
export const APP_SCHEME_ORIGIN = 'watchkeeper://open';

/**
 * The deep-link target keys that address one piece of public content. Keys,
 * not labels — they are `DeepLinkTarget.key`s, so `buildDeepLink` resolves them.
 * Module is left out on purpose: it is not in the request, and its route needs
 * a newer app build than most installs have.
 */
export type ShareableTargetKey =
  | 'reference'
  | 'brand'
  | 'manufacturer'
  | 'calibre'
  | 'article'
  | 'landing'
  | 'whats-new';

export const SHAREABLE_TARGET_KEYS: readonly ShareableTargetKey[] = [
  'reference',
  'brand',
  'manufacturer',
  'calibre',
  'article',
  'landing',
  'whats-new',
] as const;

/**
 * The share URL for an entity, or null when the id is missing or malformed (a
 * one-segment reference id, say). Each path segment is percent-encoded: ids are
 * normally slugs, but a stray space or `#` in an http URL would cut the link
 * short in every app that auto-links it, where the custom scheme never cared.
 *
 * `label` is the entity's display name. `buildDeepLink` appends it as
 * `?label=` only for the routes that want one (calibre), so passing it for
 * every type is safe; the query is already encoded and is left as built.
 */
export function buildShareUrl(
  key: ShareableTargetKey,
  id: string,
  label?: string,
  /** The target's optional number param (What's New `days`), as typed. */
  param?: string,
): string | null {
  const path = buildSharePath(key, id, label, param);
  return path === null ? null : `${APP_LINK_ORIGIN}${path}`;
}

/**
 * The same link in the app's own scheme, `watchkeeper://open/…`. Opens the app
 * directly (no store fallback when it is not installed) and does not depend on
 * the hosted apple-app-site-association listing the path.
 */
export function buildAppSchemeUrl(
  key: ShareableTargetKey,
  id: string,
  label?: string,
  param?: string,
): string | null {
  const path = buildSharePath(key, id, label, param);
  return path === null ? null : `${APP_SCHEME_ORIGIN}${path}`;
}

/** The encoded path + query both share forms put after their origin. */
function buildSharePath(
  key: ShareableTargetKey,
  id: string,
  label?: string,
  param?: string,
): string | null {
  const { value } = buildDeepLink({ mode: key, id, customUrl: '', label, param });
  if (!value) return null;
  const q = value.indexOf('?');
  const rawPath = q === -1 ? value : value.slice(0, q);
  const query = q === -1 ? '' : value.slice(q);
  const path = rawPath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${path}${query}`;
}

/** A reference is addressed by both ids — see `referenceEntityId`. */
export function referenceShareUrl(
  brandId: string,
  referenceId: string,
): string | null {
  if (!brandId.trim() || !referenceId.trim()) return null;
  return buildShareUrl('reference', referenceEntityId(brandId, referenceId));
}

/** Copy a share URL and say so. Shared by the generator and the detail pages. */
export async function copyShareUrl(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Link copied');
    return true;
  } catch (err) {
    console.error('Copy link failed:', err);
    toast.error('Could not copy the link');
    return false;
  }
}
